import {
  SliceCaseReducers,
  ValidateSliceCaseReducers,
  ActionCreatorWithoutPayload,
  createSlice,
} from '@reduxjs/toolkit';

import { IMessage } from '@sv-support-sdk/chat-shared-types/src/common/messages';
import { IMessagesState } from '@sv-support-sdk/chat-shared-types/src/store/messages';

export const initialState = {
  messages: [],
  hasUnreadMessages: false,
  typing: null,
  replied: false,
  replyToSupport: '',
} as IMessagesState;

const createMessagesSlice = <T extends IMessagesState, Reducers extends SliceCaseReducers<T>>({
  name = 'messages',
  initialState,
  reducers,
  resetState,
}: {
  name?: string;
  initialState: T;
  reducers: ValidateSliceCaseReducers<T, Reducers>;
  resetState: ActionCreatorWithoutPayload;
}) =>
  createSlice({
    name,
    initialState,
    reducers: {
      addMessages: (state, action: { payload: IMessage[] }) => {
        const newMessageIds = action.payload.map(({ id }) => id);
        const newMessagesState = state.messages.filter((el) => !newMessageIds.includes(el.id));
        newMessagesState.push(...action.payload);

        return {
          ...state,
          messages: newMessagesState.sort((el1, el2) => el1.id - el2.id),
        };
      },
      addMessage: (state, action) => {
        const { uuid, id } = action.payload;
        const messageIndex = state.messages.findIndex(
          (el) => (uuid && el.uuid === uuid) || (id && el.id === id),
        );

        const messagesCopy = [...state.messages];
        if (messageIndex < 0 || (!id && !uuid)) {
          messagesCopy.push(action.payload);
          return {
            ...state,
            messages: messagesCopy,
          };
        }

        messagesCopy[messageIndex] = { ...messagesCopy[messageIndex], ...action.payload };
        return {
          ...state,
          messages: messagesCopy,
        };
      },
      deleteMessageByType: (state, action) => ({
        ...state,
        messages: state.messages.filter((el) => el.type !== action.payload),
      }),
      deleteMessageByUuid: (state, action) => ({
        ...state,
        messages: state.messages.filter((el) => el.uuid !== action.payload),
      }),
      setUnreadMessages: (state, action) => ({
        ...state,
        hasUnreadMessages: action.payload,
      }),
      setTyping: (state, action) => ({
        ...state,
        typing: action.payload,
      }),
      setReplied: (state, action) => ({
        ...state,
        replied: action.payload,
      }),
      setReplyToSupport: (state, action) => ({
        ...state,
        replyToSupport: action.payload,
      }),
      clearTempMessages: (state) => ({
        ...state,
        messages: state.messages.filter((el) => !el.tempMessage || el.id),
      }),
      updateCallbackAnswer: (state, action) => ({
        ...state,
        messages: state.messages.map((el) =>
          el.id !== action.payload.messageId
            ? el
            : { ...el, callback: { ...el.callback, answer: action.payload.answer } },
        ),
      }),
      ...reducers,
    },
    extraReducers: (builder) => {
      builder.addCase(resetState, () => {
        return initialState;
      });
    },
  });

export default createMessagesSlice;
